import { prettifySubAccount } from '@src/providers';
import type { FilterableProperty, ISubaccount } from '@talos/kyoko';
import { IconName } from '@talos/kyoko';
import { useMemo } from 'react';

/**
 * This hook creates the filter property definitions for Trade and Hedge Sub Accounts.
 *
 * @param subAccounts The list of sub accounts to create the filters for.
 * @returns An object containing the filter property definitions for Trade and Hedge Sub Accounts.
 */
export function useHedgeTradeSubAccountFilters(subAccounts: ISubaccount[]): {
  tradeSubAccountFilter: FilterableProperty<'TradeSubAccount'>;
  hedgeSubAccountFilter: FilterableProperty<'HedgeSubAccount'>;
} {
  const subAccountsByName = useMemo(() => new Map(subAccounts.map(sa => [sa.Name, sa])), [subAccounts]);
  const tradeSubAccountFilter = useMemo(
    () => ({
      ...({
        key: 'TradeSubAccount',
        label: 'Trade Sub Account',
        field: 'TradeSubAccount',
        control: 'multi-select',
        icon: IconName.ArrowUpDown,
        options: subAccounts.map(sa => sa.Name),
        getOptionLabel: (subAccountName: string) => prettifySubAccount(subAccountName, subAccountsByName),
      } satisfies FilterableProperty<'TradeSubAccount'>),
    }),
    [subAccounts, subAccountsByName]
  );
  const hedgeSubAccountFilter = useMemo(
    () => ({
      ...({
        key: 'HedgeSubAccount',
        label: 'Hedge Sub Account',
        field: 'HedgeSubAccount',
        control: 'multi-select',
        icon: IconName.Users,
        options: subAccounts.map(sa => sa.Name),
        getOptionLabel: (subAccountName: string) => prettifySubAccount(subAccountName, subAccountsByName),
      } satisfies FilterableProperty<'HedgeSubAccount'>),
    }),
    [subAccounts, subAccountsByName]
  );
  return { tradeSubAccountFilter, hedgeSubAccountFilter };
}
